import dayjs from "dayjs";

const toCents = (value) => Math.round(Number(value) * 100);

/**
 * The cost of a stock-in line is the cost of its own batch: the batch link with a POSITIVE
 * quantity. (A line can also carry negative links, to the stock-outs that consumed it; they are
 * links of the same batch, but never the first thing to rely on.)
 */
export const getLineCostPerUnit = (line) => {
  if (line.quantity < 0) return 0;
  const links = line.inventoryBatchWarehouseStockTransactions ?? [];
  const selfLink = links.find((link) => Number(link.quantity) > 0);
  return selfLink?.inventoryBatch?.costPerUnit ?? 0;
};

/**
 * Compare the costs in a file with the lines of a stored document.
 * Every file row ends in exactly one bucket:
 *  - `changed`    a stock-in line whose cost is different
 *  - `unchanged`  a stock-in line whose cost is already that
 *  - `notFound`   a SKU the document does not have
 *  - `stockOut`   a stock-out line: it has no cost, so it is left alone
 */
export const mergeCostUpdates = (documentLines, fileLines) => {
  const lineBySku = new Map(documentLines.map((line) => [line.sku, line]));
  const result = { changed: [], unchanged: [], notFound: [], stockOut: [] };

  for (const { row, sku, costPerUnit } of fileLines) {
    const line = lineBySku.get(sku);
    if (!line) {
      result.notFound.push({ row, sku });
    } else if (line.quantity < 0) {
      result.stockOut.push({ row, sku });
    } else {
      const oldCost = getLineCostPerUnit(line);
      const entry = {
        row,
        sku,
        name: line.name,
        quantity: line.quantity,
        oldCost,
        newCost: costPerUnit,
      };
      if (toCents(oldCost) === toCents(costPerUnit)) result.unchanged.push(entry);
      else result.changed.push(entry);
    }
  }

  return result;
};

/**
 * The PATCH body that re-costs a document. PATCH replaces the whole line set: a line left out is
 * deleted and its stock reversed. So every line is sent back with its quantity and note as stored,
 * and only the cost of the lines in `changed` differs. The adjustment account is left out, so the
 * server keeps the one on the document.
 */
export const buildCostUpdatePayload = (stockAdjustment, documentLines, changed) => {
  const newCostBySku = new Map(changed.map(({ sku, newCost }) => [sku, newCost]));

  return {
    warehouseId: stockAdjustment.warehouseId,
    transactionDate: dayjs(stockAdjustment.transactionDate).format("YYYY-MM-DD"),
    note: stockAdjustment.note || null,
    stockAdjustmentProductVariants: documentLines.map((line) => ({
      sku: line.sku,
      quantity: line.quantity,
      costPerUnit:
        line.quantity < 0
          ? 0
          : (newCostBySku.get(line.sku) ?? getLineCostPerUnit(line)),
      note: line.note || null,
    })),
  };
};
